import { useState } from 'react';
import classNames from 'classnames';
// icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import logo from '../assets/logo.png';
// components
import { NavLink as RouterNavLink, useNavigate, useLocation } from 'react-router';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Clock from './Clock';
import { CircularProgress } from '@mui/material';
// API
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import { makeRequest } from '../utils/makeRequest';
// contexts
import { useEnv } from '../context/env-context';
import { useTableContext } from '../context/table-context';

function NavBar() {
  const location = useLocation();
  const navigate = useNavigate();

  const [consumersLoading, setConsumersLoading] = useState(false);
  const { audience, apiOriginConsumer } = useEnv();
  const { user, isAuthenticated, loginWithRedirect, logout, getAccessTokenSilently } = useAuth();

  const {
    noPageRefreshRef,
    updateTable,
    setSelectedAggregatorFilter,
    setSelectedConsumerIdsFilter,
  } = useTableContext();

  const [dashboardIsOpen, setDashboardIsOpen] = useState(false);

  let role;

  if (user) {
    role = user[`${audience}/metadata`].role || null;
  } else {
    role = null;
  }

  const getData = async searchText => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return null;
    }

    const config = {
      token,
      url: `${apiOriginConsumer}/get`,
      method: 'GET',
      params: {
        sortBy: 'createDate',
        sortOrder: 'desc',
        aggregatorFilter: '',
        statusFilter: '',
        bankStatusFilter: '',
        flagsFilter: '',
        searchText,
        page: 0,
        perPage: 25,
        consumerIds: null,
      },
    };

    const data = await makeRequest(config);

    return data;
  };

  const getTotalRows = async searchText => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return null;
    }

    const config = {
      token,
      url: `${apiOriginConsumer}/count`,
      method: 'GET',
      params: {
        aggregatorFilter: '',
        statusFilter: '',
        bankStatusFilter: '',
        flagsFilter: '',
        searchText,
        consumerIds: null,
      },
    };

    const data = await makeRequest(config);

    return data.count;
  };

  const handleQuickConsumerAccess = async e => {
    if (e.code === 'Enter' && e.target.value !== '') {
      if (/^\d+$/.test(e.target.value)) {
        navigate(`/consumer/${e.target.value}`);
      } else {
        try {
          setConsumersLoading(true);

          const totalRows = await getTotalRows(e.target.value);

          if (totalRows === 0) {
            toast.error('No consumers found!');
          } else {
            const data = await getData(e.target.value);

            if (data.length === 1) {
              navigate(`/consumer/${data[0].id}`);
            } else {
              setSelectedAggregatorFilter('');
              setSelectedConsumerIdsFilter({
                consumerIds: null,
                message: null,
              });
              updateTable({
                tableName: 'consumers',
                newTable: {
                  sortBy: 'createDate',
                  sortOrder: 'desc',
                  page: 0,
                  rowsPerPage: 25,
                  totalRows,
                  rows: data,
                  statusFilter: '',
                  bankStatusFilter: '',
                  flagsFilter: '',
                  mineOnly: 'TRUE',
                  search: e.target.value,
                  modeFilter: '',
                },
              });

              noPageRefreshRef.current = true;

              navigate('/consumers');
            }
          }
        } catch (err) {
          toast.error(err);
        } finally {
          setConsumersLoading(false);
        }
      }
    }
  };

  const logoutWithRedirect = () => logout({ logoutParams: { returnTo: window.location.origin } });

  return (
    <Navbar className="py-1" variant="light" expand="xl">
      <Container>
        <Navbar.Brand>
          <img alt="logo" src={logo} width="auto" height="64" />
        </Navbar.Brand>
        <Navbar.Toggle />
        <Navbar.Collapse>
          <Nav className="me-auto" navbar>
            {isAuthenticated && role === 'Superuser' && (
              <>
                <Nav.Item>
                  <Dropdown
                    navbar
                    show={dashboardIsOpen}
                    onMouseEnter={() => setDashboardIsOpen(true)}
                    onMouseLeave={() => setDashboardIsOpen(false)}
                  >
                    <Dropdown.Toggle
                      onClick={() => {
                        setDashboardIsOpen(prev => !prev);
                        navigate(`/dashboard/${localStorage.getItem('dashboard') || 'engagement'}`);
                      }}
                      className={classNames(
                        'navbar-dashboard-dropdown-toggle',
                        location.pathname?.includes('/dashboard') && 'router-link-exact-active',
                      )}
                    >
                      Dashboard
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="nav-dashboard-dropdown-menu">
                      <Dropdown.Item
                        as={RouterNavLink}
                        to="/dashboard/engagement"
                        className="px-3 py-1"
                      >
                        Engagement
                      </Dropdown.Item>
                      <Dropdown.Item
                        as={RouterNavLink}
                        to="/dashboard/advances"
                        className="px-3 py-1"
                      >
                        Advances
                      </Dropdown.Item>
                      <Dropdown.Item
                        as={RouterNavLink}
                        to="/dashboard/operational"
                        className="px-3 py-1"
                      >
                        Operational
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Nav.Item>
                <Nav.Item>
                  <RouterNavLink
                    to="/aggregators"
                    end
                    className={({ isActive }) =>
                      isActive ? 'router-link-exact-active nav-link' : 'nav-link'
                    }
                  >
                    Aggregators
                  </RouterNavLink>
                </Nav.Item>
                <Nav.Item>
                  <RouterNavLink
                    to="/consumers"
                    end
                    className={({ isActive }) =>
                      isActive ? 'router-link-exact-active nav-link' : 'nav-link'
                    }
                  >
                    Consumers
                  </RouterNavLink>
                </Nav.Item>
                <Nav.Item>
                  <RouterNavLink
                    to="/fundings"
                    end
                    className={({ isActive }) =>
                      isActive ? 'router-link-exact-active nav-link' : 'nav-link'
                    }
                  >
                    Advances
                  </RouterNavLink>
                </Nav.Item>
                <Nav.Item>
                  <RouterNavLink
                    to="/reports"
                    end
                    className={({ isActive }) =>
                      isActive ? 'router-link-exact-active nav-link' : 'nav-link'
                    }
                  >
                    Reports
                  </RouterNavLink>
                </Nav.Item>
                <Nav.Item>
                  <RouterNavLink
                    to="/settings"
                    end
                    className={() =>
                      location.pathname && location.pathname.includes('/settings')
                        ? 'router-link-exact-active nav-link'
                        : 'nav-link'
                    }
                  >
                    Settings
                  </RouterNavLink>
                </Nav.Item>
              </>
            )}
          </Nav>
          <Nav className="d-none d-md-block" navbar>
            {!isAuthenticated && (
              <Nav.Item>
                <Button className="bo-button" onClick={() => loginWithRedirect()}>
                  Log in
                </Button>
              </Nav.Item>
            )}
            {isAuthenticated && (
              <div className="d-flex align-items-center my-1">
                <Nav.Item className="me-1 mb-2 position-relative">
                  {consumersLoading && (
                    <CircularProgress
                      color="inherit"
                      size={16}
                      className="bo-consumer-quick-access-spinner"
                    />
                  )}
                  <Form.Control
                    className={`bo-consumer-quick-access ${
                      consumersLoading ? 'bo-consumer-quick-access-loading' : ''
                    }`}
                    htmlSize={24}
                    type="quickAccess"
                    name="quickAccess"
                    id="quickAccess"
                    placeholder="Consumer ID, Name or Email"
                    onKeyDown={handleQuickConsumerAccess}
                  />
                </Nav.Item>
                <Clock />
                <Dropdown navbar placement="bottom-end">
                  <Dropdown.Toggle className="m-0 nav-user-profile-toggle">
                    <img
                      src={user.picture}
                      alt="Profile"
                      className="nav-user-profile rounded-circle"
                      width="30"
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    <Dropdown.Header className="fw-bold text-dark">{user.name}</Dropdown.Header>
                    <Dropdown.Divider className="my-1" />
                    <Dropdown.Item as={RouterNavLink} to="/profile" className="dropdown-profile">
                      <FontAwesomeIcon icon="user" className="me-3" /> Profile
                    </Dropdown.Item>
                    <Dropdown.Divider className="my-1" />
                    <Dropdown.Item id="qsLogoutBtn" onClick={() => logoutWithRedirect()}>
                      <FontAwesomeIcon icon="power-off" className="me-3" /> Log out
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}
          </Nav>
          {!isAuthenticated && (
            <Nav className="d-md-none" navbar>
              <Nav.Item>
                <Button id="qsLoginBtn" color="primary" block onClick={() => loginWithRedirect({})}>
                  Log in
                </Button>
              </Nav.Item>
            </Nav>
          )}
          {isAuthenticated && (
            <Nav className="d-md-none justify-content-between" navbar>
              <Nav.Item>
                <span className="user-info">
                  <img
                    src={user.picture}
                    alt="Profile"
                    className="nav-user-profile d-inline-block rounded-circle me-3"
                    width="50"
                  />
                  <h6 className="d-inline-block">{user.name}</h6>
                </span>
              </Nav.Item>
              <Nav.Item className="d-flex align-items-center">
                <FontAwesomeIcon icon="user" className="me-3" />
                <RouterNavLink
                  to="/profile"
                  end
                  className={({ isActive }) => (isActive ? 'router-link-exact-active' : '')}
                >
                  Profile
                </RouterNavLink>
              </Nav.Item>
              <Nav.Item className="d-flex align-items-center">
                <FontAwesomeIcon icon="power-off" className="me-3" />
                <RouterNavLink to="#" id="qsLogoutBtn" onClick={() => logoutWithRedirect()}>
                  Log out
                </RouterNavLink>
              </Nav.Item>
            </Nav>
          )}
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default NavBar;
